import React from 'react';
import type { TFunction } from 'i18next';
import { formatCurrency } from '../../../utils/formatCurrency';
import { getTaxLabel, getComplianceLabel } from '../../../config/countries';
import { CTA_GRADIENT, CTA_SHADOW, SUMMARY_SHADOW } from '../constants';

export interface OrderSummaryProps {
  planName: string;
  planRegular: number;
  hasPayroll: boolean;
  payrollName: string;
  payrollRegularBase: number;
  perEmployee: number;
  perEmpCharge: number;
  employeeCount: number;
  subtotalRegular: number;
  savings: number;
  gst: number;
  total: number;
  currency: string;
  language: string;
  t: TFunction;
  onContinue: () => void;
}

export function OrderSummary({ planName, planRegular, hasPayroll, payrollName, payrollRegularBase, perEmployee, perEmpCharge, employeeCount, subtotalRegular, savings, gst, total, currency, language, t, onContinue }: OrderSummaryProps): React.ReactElement {
  return (
    <div className="sticky rounded-xl border border-hairline" style={{ background: "linear-gradient(180deg, #F9FAFB 0%, white 100%)", padding: 24, top: 20, boxShadow: SUMMARY_SHADOW }}>
      <div className="font-extrabold text-ink" style={{ fontSize: 17, letterSpacing: "-0.01em" }}>{t("cart.summary.title")}</div>
      <div className="mb-4 text-tiny text-sub">{t("cart.summary.billing")}</div>

      <div className="flex justify-between py-1.5 text-small">
        <span className="text-sub">{planName}</span>
        <span className="font-semibold text-ink">{formatCurrency(planRegular, language)}</span>
      </div>
      {hasPayroll && (
        <>
          <div className="flex justify-between py-1.5 text-small">
            <span className="text-sub">{payrollName} base</span>
            <span className="font-semibold text-ink">{formatCurrency(payrollRegularBase, language)}</span>
          </div>
          <div className="flex justify-between py-1.5 text-small">
            <span className="text-sub">{employeeCount} employees at {formatCurrency(perEmployee, language)}</span>
            <span className="font-semibold text-ink">{formatCurrency(perEmpCharge, language)}</span>
          </div>
        </>
      )}
      <div className="my-3.5 h-px bg-hairline" />

      <div className="flex justify-between py-1 text-small">
        <span className="text-sub">{t("cart.summary.subtotal")}</span>
        <span className="font-semibold text-ink">{formatCurrency(subtotalRegular, language)}</span>
      </div>
      {savings > 0 && (
        <div className="flex justify-between py-1 text-small">
          <span className="font-bold text-brand">{t("cart.summary.foundingOffer")}</span>
          <span className="font-bold text-brand">-{formatCurrency(savings, language)}</span>
        </div>
      )}
      <div className="flex justify-between py-1 text-small">
        <span className="text-sub">{getTaxLabel()}</span>
        <span className="font-semibold text-ink">{formatCurrency(gst, language)}</span>
      </div>
      <div className="my-3.5 h-px bg-hairline" />

      <div className="flex items-baseline justify-between py-1">
        <span className="text-body font-extrabold text-ink">{t("cart.summary.totalMonthly")}</span>
        <div>
          <span className="font-extrabold text-ink" style={{ fontSize: 28, letterSpacing: "-0.02em" }}>{formatCurrency(total, language)}</span>
          <span className="ml-1 text-tiny font-semibold text-muted">{currency}</span>
        </div>
      </div>

      <button
        onClick={onContinue}
        className="mt-5 block w-full cursor-pointer rounded-xl text-center text-small font-extrabold text-white font-sans"
        style={{ padding: 15, background: CTA_GRADIENT, border: "1px solid #033D22", boxShadow: CTA_SHADOW, letterSpacing: "0.01em", transition: "all 0.15s" }}
      >{t("cart.continueToPayment")} →</button>

      <div className="mt-4 border-t border-hairline pt-4">
        <div className="mb-2 flex items-start gap-2.5">
          <span className="font-extrabold text-brand" style={{ fontSize: 14, marginTop: -1 }}>✓</span>
          <div className="text-tiny text-sub leading-normal">{t("cart.trust.trial")}</div>
        </div>
        <div className="mb-2 flex items-start gap-2.5">
          <span className="font-extrabold text-brand" style={{ fontSize: 14, marginTop: -1 }}>✓</span>
          <div className="text-tiny text-sub leading-normal">{t("cart.trust.cancel")}</div>
        </div>
        <div className="flex items-start gap-2.5">
          <span className="font-extrabold text-brand" style={{ fontSize: 14, marginTop: -1 }}>✓</span>
          <div className="text-tiny text-sub leading-normal">{t("cart.trust.encryption", { compliance: getComplianceLabel() })}</div>
        </div>
      </div>
    </div>
  );
}
